import { appAlert } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// usage_tools.js — the three usage tools, as one component both stock screens
// can render.
//
// WHY A SHARED COMPONENT AND NOT A SECOND COPY
//
// Everything painful in this module so far has come from the same thing being
// implemented twice and quietly drifting: pending transfers counted on one side
// here and both sides there, pack size averaged with split packs in one place
// and without them in another, a destination's need floored on one screen and
// rounded up on the next. Every one of those cost a round of "why do the two
// screens disagree".
//
// So these tools live once each. This file is now the toolbar and the upload
// coverage table; the two big dialogs behind it have their own files
// (usage_grid.js, usage_email.js) because the detail screen opens them from its
// own toolbar without the rest.
//
// Props:
//   groupId            the organisation (localStorage "userId" on both screens)
//   members            [{ pharmacyId, pharmacyName }]
//   defaultPharmacyId  optional, pre-selects a branch in the usage grid
//   masked             hide branch names for screen sharing
//   inventory          stock documents, passed through for the grid's prices
//   onDataChanged      called after anything that could change usage figures

import React, { useState } from "react";
import PrescribedMatchReview from "./PrescribedMatchReview";
import PmrUnmatched from "./PmrUnmatched";
import UsageUpload from "./usage_upload";
import { displayName } from "./nameMask";
import UsageGridModal from "./usage_grid";
import UsageEmailModal from "./usage_email";
const API = apiUrl("");
const token = () => localStorage.getItem("authToken");
const btn = {
  padding: "8px 13px",
  marginRight: 8,
  marginBottom: 6
};

/* Shared modal shell, so the three dialogs behave identically. */
function Modal({
  title,
  subtitle,
  width = 1100,
  onClose,
  children
}) {
  return <>
      <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.5)",
      zIndex: 1600
    }} onClick={onClose} />
      <div style={{
      position: "fixed",
      top: "5vh",
      left: "50%",
      transform: "translateX(-50%)",
      width,
      maxWidth: "95vw",
      maxHeight: "88vh",
      overflowY: "auto",
      zIndex: 1610,
      background: "#fff",
      border: "1px solid #D9D9D9",
      borderRadius: 10,
      boxShadow: "0 16px 40px rgba(15,23,42,0.35)",
      padding: "16px 18px"
    }}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        marginBottom: 12
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: "1.1em"
          }}>{title}</div>
            {subtitle && <div style={{
            fontSize: "0.85em",
            color: "#5D5D5D",
            marginTop: 3,
            lineHeight: 1.5,
            maxWidth: 820
          }}>
                {subtitle}
              </div>}
          </div>
          <button onClick={onClose} style={{
          padding: "7px 14px"
        }}>Close</button>
        </div>
        {children}
      </div>
    </>;
}
export default function UsageTools({
  groupId,
  members = [],
  defaultPharmacyId = "",
  masked = false,
  inventory = null,
  onDataChanged,
  controls = "all"
}) {
  const [open, setOpen] = useState(null); // "grid" | "coverage" | "requests" | "upload" | "unmatched"

  /* --- upload coverage --------------------------------------------------- */
  const [coverage, setCoverage] = useState(null);
  const [coverageBusy, setCoverageBusy] = useState(false);
  const loadCoverage = async () => {
    try {
      setCoverageBusy(true);
      const res = await fetch(`${API}/usageUploadCoverage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId,
          months: 6
        })
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        (await appAlert(`Could not load upload coverage: HTTP ${res.status}${body ? `\n\n${body.slice(0, 300)}` : ""}`));
        return;
      }
      setCoverage(await res.json());
    } catch (e) {
      (await appAlert(`Error loading upload coverage: ${e.message}`));
    } finally {
      setCoverageBusy(false);
    }
  };
  const close = () => {
    setOpen(null);
    if (onDataChanged) onDataChanged();
  };

  /* --- render ------------------------------------------------------------ */

  return <>
      {controls !== "uploads" && <>
<button style={btn} title="Month-by-month usage for every product, as a bar chart per line. Filter by branch, search by product name. Read this to see what a branch actually gets through." onClick={() => setOpen("grid")}>
        Usage by product
      </button>
<button style={btn} title="Products a branch dispensed that never matched a dm+d product, so no figure on these screens counts them. Accept or allocate a line to bring it in." onClick={() => setOpen("unmatched")}>
        Unmatched
      </button>

      </>}
      {controls !== "analysis" && <div style={{
      display: "inline-flex",
      gap: 6,
      flexWrap: "wrap",
      alignItems: "center"
    }}>
<button style={{
        ...btn,
        background: "#16834B",
        border: "1px solid #126C3E",
        color: "#fff"
      }} title="Which branches have uploaded usage data and which have not, month by month. A branch with no usage cannot appear in the excess figures or the transfer analysis at all." onClick={() => {
        setOpen("coverage");
        loadCoverage();
      }}>
        Usage uploads
      </button>
<button style={{
        ...btn,
        background: "#16834B",
        border: "1px solid #126C3E",
        color: "#fff"
      }} title="Upload a PMR usage report or an NHS dispensing extract. The same panel as the detail screen - usage is what every excess and transfer figure is built on, so it is uploaded from wherever you happen to be." onClick={() => setOpen("upload")}>
        Upload usage files
      </button>
<button style={{
        ...btn,
        background: "#16834B",
        border: "1px solid #126C3E",
        color: "#fff"
      }} title="Emails each branch step-by-step instructions for exporting the usage report, plus a link that uploads it. The link is tied to that branch, so staff never choose a pharmacy from a list — picking the wrong one is the easiest way to corrupt this data." onClick={() => setOpen("requests")}>
        Email upload requests
      </button>
      </div>}

      <UsageGridModal open={open === "grid"} onClose={close} groupId={groupId} members={members} defaultPharmacyId={defaultPharmacyId} masked={masked} inventory={inventory} />

      {open === "upload" && <Modal title="Upload usage data" onClose={close}>
          <UsageUpload groupId={groupId} members={members} masked={masked} onUploaded={onDataChanged} />
        </Modal>}

      {open === "prescribedMatch" && <PrescribedMatchReview groupId={groupId} onClose={close} onChanged={onDataChanged} />}
      {open === "unmatched" && <PmrUnmatched groupId={groupId} members={members} onClose={close} />}

      {/* ---- upload coverage ---- */}
      {open === "coverage" && <Modal title="Usage uploads by branch" width={1100} onClose={close}>
          <div style={{
        background: "#f6f8fa",
        border: "1px solid #dfe3e8",
        padding: "8px 10px",
        margin: "10px 0",
        fontSize: "0.9em",
        lineHeight: 1.45
      }}>
            Each cell is the <strong>number of product lines</strong> stored for that branch in
            that month, and beneath it the number of <strong>trading days</strong> that month
            had any dispensing at all. The current month is not shown: usage is loaded a
            completed month at a time, so it would read as empty for everyone and tell you
            nothing. A branch with no data cannot appear in the excess figures or the transfer
            analysis at all.
            <br />
            Coverage uses the lower of the estate median and Monday–Friday days,
            excluding England/Wales bank holidays, with two days&apos; allowance.
            Warnings indicate fewer dispensing days than this baseline or a file marked
            partial by the importer. Check the export dates before asking for another upload.
            A dash means the month was loaded
            before trading days were recorded.
          </div>

          {coverageBusy && <div>Loading&hellip;</div>}

          {!coverageBusy && coverage && <table border="1" cellPadding="6" style={{
        width: "100%",
        fontSize: "0.9em"
      }}>
              <thead style={{
          position: "sticky",
          top: 0,
          background: "#eee"
        }}>
                <tr>
                  <th style={{
              textAlign: "left"
            }}>Pharmacy</th>
                  {(coverage.months || []).map(m => <th key={m.month} style={{
              whiteSpace: "nowrap"
            }}>
                      {String(m.label || "").replace(" 20", " ")}
                      {Number.isFinite(m.typicalTradingDays) && <div style={{
                fontSize: "0.75em",
                fontWeight: "normal",
                color: "#666"
              }}>
                          {m.typicalTradingDays}d baseline
                        </div>}
                    </th>)}
                  <th title="How many of the six months hold any data at all.">Months</th>
                </tr>
              </thead>
              <tbody>
                {(coverage.pharmacies || []).map(p => <tr key={p.pharmacyId} style={{
            background: p.monthsLoaded === 0 ? "#fff4f4" : undefined
          }}>
                    <td>{displayName(p.pharmacyName, masked)}</td>
                    {(p.months || []).map((c, i) => {
              const typical = (coverage.months || [])[i]?.typicalTradingDays;
              // The API caps the estate median at a weekday calendar
              // baseline. Keep two days' grace and importer partial flags.
              const short = c.items > 0 && Number.isFinite(c.tradingDays) && Number.isFinite(typical) && c.tradingDays < typical - 2;
              const flagged = c.items > 0 && (short || c.partial);
              return <td key={c.month} style={{
                textAlign: "center",
                background: c.items === 0 ? "#f4f4f4" : flagged ? "#fff6e5" : undefined,
                color: c.items === 0 ? "#aaa" : undefined
              }} title={c.items === 0 ? "Nothing stored for this month" : `${c.items} product lines, ${(c.units || 0).toLocaleString()} units` + (Number.isFinite(c.tradingDays) ? `\n${c.tradingDays} trading days` + (Number.isFinite(typical) ? ` (coverage baseline ${typical}, two days' allowance)` : "") + (c.firstDay && c.lastDay ? `, from the ${c.firstDay}${c.daysInMonth ? ` to the ${c.lastDay} of ${c.daysInMonth}` : ""}` : "") : "\nTrading days not recorded - loaded before this was stored") + (c.why ? `\n${c.why}` : "") + (c.partial ? "\nThis month is only partly covered by the file it came from" : "") + ((c.sources || []).length ? `\nFrom ${c.sources.join(" + ")}` : "") + (c.apportioned ? "\nFigures estimated across a period" : "")}>
                          {c.items === 0 ? "\u2014" : (c.items || 0).toLocaleString()}
                          {c.apportioned && <span style={{
                  color: "#a00"
                }}> *</span>}
                          {c.items > 0 && <div style={{
                  fontSize: "0.8em",
                  marginTop: 2,
                  color: flagged ? "#a05a00" : "#888",
                  fontWeight: flagged ? "bold" : "normal"
                }}>
                              {Number.isFinite(c.tradingDays) ? `${c.tradingDays}d` : "\u2013"}
                              {flagged && " \u26a0"}
                            </div>}
                        </td>;
            })}
                    <td style={{
              textAlign: "center",
              fontWeight: "bold",
              color: p.monthsLoaded === 0 ? "#c00" : undefined
            }}>
                      {p.monthsLoaded}/{(coverage.months || []).length}
                    </td>
                  </tr>)}
              </tbody>
            </table>}

          {!coverageBusy && coverage && (coverage.pharmacies || []).some(p => (p.months || []).some(c => c.apportioned)) && <div style={{
        marginTop: "8px",
        fontSize: "0.85em",
        color: "#a00"
      }}>
                * those months came from an older PMR report that gave one total for a whole
                period, so the split between months is an estimate. Re-import that branch to
                replace them with measured figures.
              </div>}
        </Modal>}

      <UsageEmailModal open={open === "requests"} onClose={close} groupId={groupId} masked={masked} />
    </>;
}
